import { ASSET_SHARE_MAX_VISITS, type PublicAsset } from "@fusiqo/vozqi-shared";
import { useCallback, useEffect, useRef, useState } from "react";
import { AssetVisits } from "./asset-visits";
import { assetPhotoSrc, assetWhatsappHref, nextLine, tokenFromAssetPath } from "./asset-history-view";
import { PublicHeader, PublicHeaderSkeleton } from "./public-header";
import { Notice, Photo, Photos, Viewer, type PhotoSrc } from "./SharePage";
import { WhatsAppMark } from "./whatsapp-mark";
import { BrandLogo } from "../ui/brand-logo";

/**
 * THE PUBLIC PAGE OF AN OBJECT'S HISTORY (spec bitácora por enlace §9.2): what
 * the workshop's customer sees after tapping the link it sent over WhatsApp
 * -- the object, its photos and each delivered visit's day, lines and «Lo que
 * se hizo». Never money: the view is a closed list (`PublicAsset`).
 *
 * The same entry, header, slider and floating WhatsApp button as an item's
 * page (SharePage.tsx); the data comes from the Worker on the same origin
 * (`/e/api/<token>`).
 *
 * EACH VISIT SHOWS ITS PHOTOS: the ones tagged with this object on that
 * ticket, all of them, wrapping. A tap opens that visit's photos large, as
 * the object's own open from the slider.
 *
 * THE PAGE ITSELF COUNTS THE OPEN: once it has drawn, it posts the beacon
 * (`/e/api/<token>/abierto`) -- once per load. WhatsApp's crawler reads the
 * data for the preview and runs no script, so it never passes for the
 * customer opening the link.
 */
type State =
  | { kind: "loading" }
  | { kind: "gone" }
  | { kind: "failed" }
  | { kind: "ready"; view: PublicAsset };

const FOOT_HREF = "https://vozqi.com/tecnicos?utm_source=asset-share";

export function AssetHistoryPage() {
  const token = tokenFromAssetPath(window.location.pathname);
  const [state, setState] = useState<State>(token === null ? { kind: "gone" } : { kind: "loading" });
  // StrictMode runs effects twice in development; the open is counted once per load.
  const counted = useRef(false);

  const load = useCallback(async () => {
    if (token === null) return;
    setState({ kind: "loading" });
    try {
      const response = await fetch(`/e/api/${token}`);
      if (response.status === 404) return setState({ kind: "gone" });
      if (!response.ok) return setState({ kind: "failed" });
      setState({ kind: "ready", view: (await response.json()) as PublicAsset });
    } catch {
      setState({ kind: "failed" });
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (state.kind !== "ready" || token === null) return;
    document.title = `${state.view.asset.label} · ${state.view.business.name}`;
    if (counted.current) return;
    counted.current = true;
    void fetch(`/e/api/${token}/abierto`, { method: "POST", keepalive: true }).catch(() => undefined);
  }, [state, token]);

  if (state.kind === "loading") return <Skeleton />;
  if (state.kind === "gone") return <Notice title="Este enlace ya no está disponible." footHref={FOOT_HREF} />;
  if (state.kind === "failed" || token === null) {
    return (
      <Notice title="No pudimos cargar esto" footHref={FOOT_HREF}>
        <p>Revisa tu conexión e inténtalo otra vez.</p>
        <button type="button" className="share-retry" onClick={() => void load()}>
          Reintentar
        </button>
      </Notice>
    );
  }
  return <History view={state.view} token={token} />;
}

function History({ view, token }: { view: PublicAsset; token: string }) {
  // The photos the viewer walks -- the object's own, or one visit's -- and where it opened.
  const [viewing, setViewing] = useState<{ photos: string[]; start: number } | null>(null);
  const whatsapp = assetWhatsappHref(view);
  const srcOf: PhotoSrc = (id, size) => assetPhotoSrc(token, id, size);
  const { asset } = view;

  return (
    <div className="share-page">
      <PublicHeader
        name={view.business.name}
        logoSrc={view.business.hasLogo ? `/e/${token}/logo` : null}
        heading={false}
        shopHref={null}
      />
      <div className={whatsapp === null ? "share" : "share share--with-fab"}>
        <main>
          <section className="share-body asset-head">
            <h1 className="share-name">{asset.label}</h1>
            {asset.archived && <p className="asset-archived">Ya no está en uso</p>}
          </section>

          {asset.photos.length > 0 && (
            <Photos photos={asset.photos} srcOf={srcOf} name={asset.label} onOpen={(start) => setViewing({ photos: asset.photos, start })} />
          )}

          <section className="share-body asset-history" aria-labelledby="asset-history-title">
            {view.next !== null && <p className="asset-next">{nextLine(view.next)}</p>}
            <h2 id="asset-history-title" className="asset-history-title">
              Historial
            </h2>
            {view.visits.length === 0 ? (
              <p className="asset-history-empty">Todavía no hay trabajos anotados.</p>
            ) : (
              <AssetVisits
                visits={view.visits}
                photo={(id, at, photos) => (
                  <Photo
                    srcOf={srcOf}
                    id={id}
                    size="thumb"
                    alt={asset.label}
                    eager={false}
                    onOpen={() => setViewing({ photos, start: at })}
                  />
                )}
              />
            )}
            {view.truncated && <p className="asset-history-more">Se muestran las {ASSET_SHARE_MAX_VISITS} más recientes.</p>}
          </section>
        </main>

        <footer className="share-foot">
          <a href={FOOT_HREF} rel="noopener">
            Hecho con <BrandLogo height={16} />
          </a>
        </footer>

        {whatsapp !== null && viewing === null && (
          <a
            className="share-fab"
            href={whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Escribir por WhatsApp"
            title="Escribir por WhatsApp"
          >
            <WhatsAppMark />
          </a>
        )}

        {viewing !== null && (
          <Viewer photos={viewing.photos} srcOf={srcOf} name={asset.label} start={viewing.start} onClose={() => setViewing(null)} />
        )}
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="share-page" role="status" aria-busy="true" aria-label="Cargando">
      <PublicHeaderSkeleton />
      <div className="share">
        <section className="share-body">
          <span className="share-skeleton share-skeleton--title" />
          <span className="share-skeleton share-skeleton--line" style={{ width: "35%" }} />
          <span className="share-skeleton share-skeleton--line" style={{ width: "60%", marginTop: 24 }} />
        </section>
      </div>
    </div>
  );
}
